import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  Stack,
  Chip,
  Paper,
  Button,
  Fade,
  Zoom,
  Divider,
  TextField,
} from '@mui/material';
import DiamondIcon from '@mui/icons-material/Diamond';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import PaymentsIcon from '@mui/icons-material/Payments';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SecurityIcon from '@mui/icons-material/Security';
import SpeedIcon from '@mui/icons-material/Speed';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import PublicIcon from '@mui/icons-material/Public';
import SendIcon from '@mui/icons-material/Send';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

// Premium Color Palette
const colors = {
  navy: '#0a1128',
  darkNavy: '#060b1f',
  gold: '#c9a03d',
  lightGold: '#d4af37',
  darkGold: '#a0782c',
  charcoal: '#1a1a2e',
  lightCharcoal: '#16213e',
  pearl: '#f5f5f0',
  darkPearl: '#e8e8e0',
  white: '#ffffff',
  textDark: '#1a1a2e',
  textLight: '#6b6b6b',
  premiumDark: '#0d1117',
  premiumGold: '#b8942e',
};

const About = () => {
  const newsItems = [
    {
      title: "4 Expenses You Should Never Put on Your Business Credit Card",
      excerpt: "Although the majority of credit cards in circulation are for consumers, the popularity of business credit cards is on the upswing. According to a 2018 report by the Mercator Advisory Group, business credit card usage has grown significantly...",
      date: "December 15, 2024",
    },
    {
      title: "The Secret to Mastercard's Success",
      excerpt: "For investors, the ideal stock would probably be one that operates in a growing market, has a sustainable competitive advantage, and faces little competition. Enter Mastercard (NYSE:MA)...",
      date: "December 10, 2024",
    },
    {
      title: "Could Mastercard Be a Millionaire-Maker Stock?",
      excerpt: "While Mastercard (NYSE:MA) has corporate roots more than a half century old and has existed under the current name since the late 1970s, it only went public about 13 years ago...",
      date: "December 5, 2024",
    },
  ];

  const coreValues = [
    { icon: <SecurityIcon sx={{ fontSize: 28 }} />, title: "Security First", desc: "Bank-grade encryption protecting your assets" },
    { icon: <SpeedIcon sx={{ fontSize: 28 }} />, title: "Lightning Fast", desc: "Instant transactions and real-time updates" },
    { icon: <WorkspacePremiumIcon sx={{ fontSize: 28 }} />, title: "Premium Service", desc: "Dedicated concierge support 24/7" },
    { icon: <PublicIcon sx={{ fontSize: 28 }} />, title: "Global Reach", desc: "Accepted in over 190 countries" },
  ];

  return (
    <Box>
      {/* Hero Section - Increased Height with Reduced Overlay Opacity */}
      <Box 
        sx={{ 
          position: 'relative',
          py: { xs: 12, md: 16 },
          minHeight: { xs: 450, md: 700},
          color: 'white', 
          textAlign: 'center',
          display: 'flex',
          alignItems: 'center',
          backgroundImage: 'url("/images/herobackground.jpg")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(13,17,23,0.4) 0%, rgba(26,26,46,0.3) 100%)',
          }
        }}
      >
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Fade in timeout={1000}>
            <Box>
              <Avatar 
                src="/logo/logo.png" 
                alt="Diamond Card"
                sx={{ 
                  width: 120, 
                  height: 120, 
                  mx: 'auto', 
                  mb: 3,
                  backgroundColor: 'transparent',
                  '& img': { objectFit: 'contain' }
                }}
                variant="square"
              />
            </Box>
          </Fade>
        </Container>
      </Box>

      {/* Main About Content */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Fade in timeout={800}>
          <Box>
            <Box sx={{ textAlign: 'center', mb: 5 }}>
              <Chip 
                label="ABOUT US" 
                sx={{ 
                  bgcolor: colors.premiumGold, 
                  color: colors.navy, 
                  fontWeight: 700,
                  px: 3,
                  py: 1.5,
                  fontSize: '0.85rem',
                  letterSpacing: 1.5,
                  mb: 2,
                }}
              />
              <Typography 
                variant="h2" 
                sx={{ 
                  fontSize: { xs: '2rem', md: '3rem' }, 
                  mt: 2,
                  mb: 2, 
                  color: colors.navy, 
                  fontWeight: 800,
                  letterSpacing: -0.5,
                }}
              >
                DIAMOND CARD CORPORATION
              </Typography>
              <Typography 
                variant="h5" 
                sx={{ color: colors.premiumGold, fontWeight: 600, fontSize: '1.3rem' }}
              >
                The First Choice Alternate Bank
              </Typography>
            </Box>

            <Paper 
              elevation={0} 
              sx={{ 
                p: { xs: 4, md: 5 }, 
                borderRadius: 4, 
                boxShadow: '0 20px 40px rgba(0,0,0,0.05)',
                bgcolor: colors.white,
              }}
            >
              <Typography variant="body1" paragraph sx={{ fontSize: '1rem', lineHeight: 1.7, color: colors.textDark }}>
                Diamond Card Corporation provides services worldwide. We have established the Diamond Network, 
                allowing merchants and service providers to perform financial transactions when their consumers 
                present a Diamond card.
              </Typography>
              <Typography variant="body1" paragraph sx={{ fontSize: '1rem', lineHeight: 1.7, color: colors.textDark }}>
                Initial rollout of the Diamond Card is in the United States; any organization, merchant, or community 
                is welcome to issue personalised prepaid, debit, or credit cards to their client base or members. 
                Transactions made within the community carry no fees. Payments and transfers can be made between 
                community members at no cost.
              </Typography>
              <Typography variant="body1" paragraph sx={{ fontSize: '1rem', lineHeight: 1.7, color: colors.textDark }}>
                Diamond Card Corporation is the best alternative to effectively manage your money, providing you 
                with all the necessary access and tools. Be your own private bank with online access and our Mobile APP. 
                All of our cards also include the Tap feature for payment ease.
              </Typography>
              <Typography variant="body1" paragraph sx={{ fontSize: '1rem', lineHeight: 1.7, color: colors.textDark }}>
                Whether you want to build a new home, improve your credit score or consolidate your debt – Diamond Cards can help. 
                From interest-free balance transfers to cashback and rewards, compare our Diamond Cards to find the one that's right for you.
              </Typography>
              <Divider sx={{ my: 3 }} />
              <Typography variant="body1" sx={{ fontSize: '1rem', lineHeight: 1.7, color: colors.premiumGold, fontWeight: 500, fontStyle: 'italic', textAlign: 'center' }}>
                "With a Diamond Card you can enjoy the freedom to handle all your finances with three different options available to all our clients."
              </Typography>
            </Paper>
          </Box>
        </Fade>
      </Container>

      {/* Core Values Section - 4 cards in one row */}
      <Box sx={{ bgcolor: colors.lightCharcoal, py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" textAlign="center" sx={{ fontWeight: 700, color: colors.white, mb: 2, fontSize: { xs: '1.8rem', md: '2.2rem' } }}>
            Our Core Values
          </Typography>
          <Typography variant="body1" textAlign="center" sx={{ mb: 5, color: colors.white, opacity: 0.7 }}>
            The principles that guide everything we do
          </Typography>
          <Grid container spacing={3} alignItems="stretch">
            {coreValues.map((value, idx) => (
              <Zoom in timeout={400 + idx * 200} key={idx}>
                <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex' }}>
                  <Card sx={{ 
                    textAlign: 'center', 
                    p: 2.5, 
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 3, 
                    bgcolor: colors.charcoal, 
                    transition: 'all 0.3s', 
                    '&:hover': { 
                      transform: 'translateY(-5px)', 
                      boxShadow: `0 15px 30px ${colors.premiumGold}20`,
                      borderTop: `2px solid ${colors.premiumGold}`,
                    } 
                  }}>
                    <Avatar sx={{ bgcolor: colors.premiumGold, color: colors.navy, width: 55, height: 55, mx: 'auto', mb: 1.5 }}>
                      {value.icon}
                    </Avatar>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: colors.white, mb: 1 }}>
                      {value.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: colors.white, opacity: 0.7, fontSize: '0.8rem' }}>
                      {value.desc}
                    </Typography>
                  </Card>
                </Grid>
              </Zoom>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Card Types Section - 3 cards in one row */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Typography variant="h3" textAlign="center" sx={{ fontWeight: 700, color: colors.navy, mb: 2, fontSize: { xs: '1.6rem', md: '2rem' } }}>
          Premium Card Portfolio
        </Typography>
        <Typography variant="body1" textAlign="center" sx={{ mb: 4, color: colors.textLight, fontSize: '0.9rem' }}>
          Three distinct options tailored to your lifestyle
        </Typography>
        <Grid container spacing={3} alignItems="stretch">
          {[
            { icon: <CreditCardIcon sx={{ fontSize: 32 }} />, title: "DEBIT CARDS", desc: "Access to your money wherever you are and whenever you want." },
            { icon: <PaymentsIcon sx={{ fontSize: 32 }} />, title: "CREDIT CARDS", desc: "Convenience. Spend safely in-store, online or overseas." },
            { icon: <CardGiftcardIcon sx={{ fontSize: 32 }} />, title: "PRE-PAID CARDS", desc: "Load and pay. A more convenient and safer way to pay." },
          ].map((card, idx) => (
            <Grid item xs={12} md={4} key={idx} sx={{ display: 'flex' }}>
              <Zoom in timeout={400 + idx * 200}>
                <Card sx={{ 
                  textAlign: 'center', 
                  p: 3, 
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 3, 
                  transition: 'all 0.3s', 
                  borderTop: `3px solid ${colors.premiumGold}`, 
                  '&:hover': { 
                    transform: 'translateY(-6px)', 
                    boxShadow: `0 15px 30px ${colors.premiumGold}30`,
                  } 
                }}>
                  <Avatar sx={{ bgcolor: colors.premiumGold, color: colors.navy, width: 60, height: 60, mx: 'auto', mb: 1.5 }}>
                    {card.icon}
                  </Avatar>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: colors.navy, fontSize: '1.1rem' }}>
                    {card.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                    {card.desc}
                  </Typography>
                </Card>
              </Zoom>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* CTA Section */}
      <Box sx={{ bgcolor: colors.navy, py: 8 }}>
        <Container maxWidth="md">
          <Paper elevation={0} sx={{ p: 5, borderRadius: 4, textAlign: 'center', bgcolor: colors.charcoal, border: `1px solid ${colors.premiumGold}33` }}>
            <Typography variant="h3" gutterBottom sx={{ fontWeight: 700, color: colors.premiumGold, fontSize: { xs: '1.5rem', md: '2rem' } }}>
              Get the Card that fits you
            </Typography>
            <Typography variant="body1" paragraph sx={{ mb: 3, color: colors.white, opacity: 0.8 }}>
              Ready to bank your way? Order a card now and receive it from 14 business days!
            </Typography>
            
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="center" sx={{ mb: 3, flexWrap: 'wrap' }}>
              <Chip label="✓ No minimum balance" sx={{ bgcolor: `${colors.premiumGold}1A`, color: colors.premiumGold, fontWeight: 600 }} />
              <Chip label="✓ No activation fee" sx={{ bgcolor: `${colors.premiumGold}1A`, color: colors.premiumGold, fontWeight: 600 }} />
              <Chip label="✓ No credit check" sx={{ bgcolor: `${colors.premiumGold}1A`, color: colors.premiumGold, fontWeight: 600 }} />
            </Stack>
            
            <Typography variant="h4" sx={{ color: colors.premiumGold, fontWeight: 700, mb: 3, fontSize: '1.5rem' }}>
              Only a one time fee of $50 for your personalized Diamond Card!
            </Typography>
            
            <Button 
              variant="contained" 
              sx={{ 
                bgcolor: colors.premiumGold,
                color: colors.navy,
                fontWeight: 700, 
                px: 5, 
                py: 1.5,
                fontSize: '1rem',
                borderRadius: 50,
                '&:hover': {
                  bgcolor: colors.lightGold,
                  transform: 'translateY(-2px)',
                  boxShadow: `0 8px 20px ${colors.premiumGold}40`,
                },
                transition: 'all 0.3s ease',
              }}
            >
              Order Your Diamond Card
            </Button>
          </Paper>
        </Container>
      </Box>

      {/* Where We Work Section - 2 offices in one row */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h3" textAlign="center" sx={{ fontWeight: 700, color: colors.navy, mb: 2, fontSize: { xs: '1.8rem', md: '2.2rem' } }}>
          WHERE WE WORK
        </Typography>
        <Typography variant="h5" textAlign="center" sx={{ color: colors.premiumGold, fontWeight: 600, mb: 5, fontSize: '1.2rem', letterSpacing: 2 }}>
          OUR OFFICES
        </Typography>
        
        <Grid container spacing={4} alignItems="stretch">
          <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
            <Zoom in timeout={400}>
              <Paper sx={{ 
                p: 3.5, 
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 3, 
                transition: 'all 0.3s', 
                '&:hover': { 
                  transform: 'translateY(-5px)', 
                  boxShadow: `0 15px 30px ${colors.premiumGold}20`,
                  borderLeft: `3px solid ${colors.premiumGold}`,
                } 
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: colors.premiumGold, width: 50, height: 50, mr: 1.5 }}>
                    <LocationOnIcon sx={{ fontSize: 28, color: colors.navy }} />
                  </Avatar>
                  <Typography variant="h5" color={colors.navy} fontWeight={700} fontSize="1.3rem">United States</Typography>
                </Box>
                <Typography variant="body2" paragraph sx={{ mb: 1.5, lineHeight: 1.6, color: colors.textDark }}>
                  Suite 1200 1000 N. West Street,<br />
                  Downtown Wilmington,<br />
                  Delaware, 19801
                </Typography>
                <Divider sx={{ my: 1 }} />
                <Typography variant="body2">
                  <strong style={{ color: colors.premiumGold }}>Phone:</strong> +1-302-295-3810
                </Typography>
              </Paper>
            </Zoom>
          </Grid>
          <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
            <Zoom in timeout={600}>
              <Paper sx={{ 
                p: 3.5, 
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 3, 
                transition: 'all 0.3s', 
                '&:hover': { 
                  transform: 'translateY(-5px)', 
                  boxShadow: `0 15px 30px ${colors.premiumGold}20`,
                  borderLeft: `3px solid ${colors.premiumGold}`,
                } 
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: colors.premiumGold, width: 50, height: 50, mr: 1.5 }}>
                    <LocationOnIcon sx={{ fontSize: 28, color: colors.navy }} />
                  </Avatar>
                  <Typography variant="h5" color={colors.navy} fontWeight={700} fontSize="1.3rem">South Africa</Typography>
                </Box>
                <Typography variant="body2" paragraph sx={{ mb: 1.5, lineHeight: 1.6, color: colors.textDark }}>
                  341 Beyers Naude Drive,<br />
                  Northcliff Johannesburg Drive,<br />
                  South Africa, 2195
                </Typography>
                <Divider sx={{ my: 1 }} />
                <Typography variant="body2">
                  <strong style={{ color: colors.premiumGold }}>Phone:</strong> +27 72 010 0332
                </Typography>
              </Paper>
            </Zoom>
          </Grid>
        </Grid>
      </Container>

      {/* Get in Touch & News Section */}
      <Box sx={{ bgcolor: colors.navy, py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h4" sx={{ color: colors.premiumGold, mb: 1, fontWeight: 700, textAlign: 'center', letterSpacing: 3 }}>
            DIAMOND NETWORK
          </Typography>
          <Typography variant="h3" sx={{ color: colors.white, mb: 2, fontWeight: 700, textAlign: 'center', fontSize: { xs: '1.8rem', md: '2.2rem' } }}>
            GET IN TOUCH
          </Typography>
          <Typography variant="body1" sx={{ color: colors.white, mb: 5, opacity: 0.8, textAlign: 'center', maxWidth: 550, mx: 'auto' }}>
            Feel free to make contact with any questions you may have. We will get back to you within 24 hours.
          </Typography>
          
          <Grid container spacing={5}>
            {/* Contact Form */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 4, bgcolor: colors.charcoal, borderRadius: 3 }}>
                <Stack spacing={3}>
                  <TextField 
                    label="Name*" 
                    fullWidth 
                    required
                    sx={{ 
                      '& .MuiOutlinedInput-root': { color: colors.white, '& fieldset': { borderColor: `${colors.white}33` }, '&:hover fieldset': { borderColor: colors.premiumGold } },
                      '& .MuiInputLabel-root': { color: `${colors.white}99` }
                    }}
                  />
                  <TextField 
                    label="Email*" 
                    type="email" 
                    fullWidth 
                    required
                    sx={{ 
                      '& .MuiOutlinedInput-root': { color: colors.white, '& fieldset': { borderColor: `${colors.white}33` }, '&:hover fieldset': { borderColor: colors.premiumGold } },
                      '& .MuiInputLabel-root': { color: `${colors.white}99` }
                    }}
                  />
                  <TextField 
                    label="Phone" 
                    fullWidth 
                    sx={{ 
                      '& .MuiOutlinedInput-root': { color: colors.white, '& fieldset': { borderColor: `${colors.white}33` }, '&:hover fieldset': { borderColor: colors.premiumGold } },
                      '& .MuiInputLabel-root': { color: `${colors.white}99` }
                    }}
                  />
                  <TextField 
                    label="Message" 
                    multiline 
                    rows={4} 
                    fullWidth 
                    sx={{ 
                      '& .MuiOutlinedInput-root': { color: colors.white, '& fieldset': { borderColor: `${colors.white}33` }, '&:hover fieldset': { borderColor: colors.premiumGold } },
                      '& .MuiInputLabel-root': { color: `${colors.white}99` }
                    }}
                  />
                  <Button 
                    variant="contained" 
                    fullWidth 
                    size="large"
                    endIcon={<SendIcon />}
                    sx={{ 
                      bgcolor: colors.premiumGold,
                      color: colors.navy,
                      fontWeight: 700, 
                      py: 1.5,
                      '&:hover': { bgcolor: colors.lightGold, transform: 'translateY(-2px)' },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    Send Message
                  </Button>
                </Stack>
              </Paper>
            </Grid>

            {/* News Section */}
            <Grid item xs={12} md={6}>
              <Typography variant="h5" sx={{ color: colors.premiumGold, mb: 2, fontWeight: 700, fontSize: '1.3rem' }}>
                DIAMOND CARDS NEWS
              </Typography>
              <Typography variant="body2" sx={{ color: colors.white, mb: 3, opacity: 0.8 }}>
                Keeping you updated with the latest financial insights
              </Typography>
              
              <Stack spacing={2.5}>
                {newsItems.map((item, idx) => (
                  <Paper key={idx} sx={{ 
                    p: 2.5, 
                    bgcolor: colors.charcoal, 
                    borderRadius: 2, 
                    transition: 'all 0.3s', 
                    '&:hover': { transform: 'translateX(5px)', bgcolor: `${colors.charcoal}CC`, borderLeft: `2px solid ${colors.premiumGold}` }
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <CalendarTodayIcon sx={{ fontSize: 12, color: colors.premiumGold, mr: 0.5 }} />
                      <Typography variant="caption" sx={{ color: colors.premiumGold }}>
                        {item.date}
                      </Typography>
                    </Box>
                    <Typography variant="subtitle2" sx={{ color: colors.white, fontWeight: 700, mb: 1, fontSize: '0.9rem' }}>
                      {item.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: `${colors.white}99`, lineHeight: 1.5, display: 'block', mb: 1.5, fontSize: '0.75rem' }}>
                      {item.excerpt.substring(0, 120)}...
                    </Typography>
                    <Button 
                      size="small" 
                      sx={{ color: colors.premiumGold, textTransform: 'none', fontWeight: 600, p: 0, '&:hover': { transform: 'translateX(3px)' }, transition: 'transform 0.2s' }}
                      endIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />}
                    >
                      Read More
                    </Button>
                  </Paper>
                ))}
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Diamond Network Footer Bar */}
      <Box sx={{ bgcolor: colors.premiumGold, py: 3.5, textAlign: 'center' }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: colors.navy, letterSpacing: 4, fontSize: { xs: '1.3rem', md: '1.8rem' } }}>
          DIAMOND NETWORK
        </Typography>
        <Typography variant="caption" sx={{ color: colors.navy, opacity: 0.7, mt: 0.5, display: 'block', letterSpacing: 1.5 }}>
          Connecting global financial excellence
        </Typography>
      </Box>
    </Box>
  );
};

export default About;